import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import MonthPickerButton from 'components/month-picker-button'
import SectionTitle from 'components/section-title'
import ArrowDownwardIcon from 'components/material-symbol/icons/ArrowDownward'
import ArrowUpwardIcon from 'components/material-symbol/icons/ArrowUpward'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import StatCard from 'pages/pga-coach/coach-analytics/components/stat-card'
import { DeltaSkeleton } from './loading-skeleton'
import { GET_BUSINESS_AT_A_GLANCE } from './query'
import {
  MONTH_VALUE_FORMAT,
  getMonthValue,
  getTiles,
  getTimeRange,
} from './utils'
import useStyles from './styles'

const Delta = ({ deltaPercent, comparisonLabel }) => {
  const classes = useStyles()

  if (deltaPercent === null) return null

  if (deltaPercent === 0) {
    return (
      <Typography
        component="p"
        className={`${classes.delta} ${classes.deltaFlat}`}
      >
        No change vs. {comparisonLabel}
      </Typography>
    )
  }

  const isUp = deltaPercent > 0
  const Arrow = isUp ? ArrowUpwardIcon : ArrowDownwardIcon

  return (
    <Typography
      component="p"
      className={`${classes.delta} ${
        isUp ? classes.deltaUp : classes.deltaDown
      }`}
    >
      <Arrow aria-hidden />
      {Math.abs(deltaPercent)}% vs. {comparisonLabel}
    </Typography>
  )
}

const Tile = ({ tile, comparisonLabel, loading }) => {
  const classes = useStyles()

  return (
    <Box className={classes.tile} data-testid={`glance-${tile.id}`}>
      <StatCard label={tile.label} value={tile.value} loading={loading}>
        {loading ? (
          <DeltaSkeleton />
        ) : (
          <Delta
            deltaPercent={tile.deltaPercent}
            comparisonLabel={comparisonLabel}
          />
        )}
      </StatCard>
    </Box>
  )
}

export const BusinessAtAGlanceView = ({
  analytics,
  loading,
  month,
  currentMonth = getMonthValue(),
  onMonthChange,
}) => {
  const classes = useStyles()
  const selectedMonth = DateTime.fromFormat(month, MONTH_VALUE_FORMAT)
  const comparisonLabel =
    month === currentMonth
      ? 'last month'
      : selectedMonth.minus({ months: 1 }).toFormat('LLLL')

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <SectionTitle size="card" className={classes.cardTitle}>
          Your business at a glance
        </SectionTitle>
        <MonthPickerButton
          value={selectedMonth}
          onChange={picked => onMonthChange(getMonthValue(picked))}
          labelFormat="LLLL yyyy"
          buttonProps={{
            variant: 'text',
            color: 'default',
            className: classes.monthButton,
            startIcon: null,
            endIcon: <ExpandMoreIcon aria-hidden />,
          }}
        />
      </Box>
      <Box className={classes.grid}>
        {getTiles(analytics, month).map(tile => (
          <Tile
            key={tile.id}
            tile={tile}
            comparisonLabel={comparisonLabel}
            loading={loading}
          />
        ))}
      </Box>
    </Box>
  )
}

BusinessAtAGlanceView.propTypes = {
  analytics: PropTypes.object,
  loading: PropTypes.bool,
  month: PropTypes.string.isRequired,
  currentMonth: PropTypes.string,
  onMonthChange: PropTypes.func.isRequired,
}

// Optional card: it disappears when the analytics request fails.
const BusinessAtAGlance = () => {
  const [month, setMonth] = useState(() => getMonthValue())
  const { data, loading, error } = useQuery(GET_BUSINESS_AT_A_GLANCE, {
    variables: { timeRange: getTimeRange(month) },
  })

  useEffect(() => {
    if (error) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['business-at-a-glance'] })
    }
  }, [error])

  if (error) return null

  return (
    <BusinessAtAGlanceView
      analytics={data?.pgaCoachAnalytics}
      loading={loading}
      month={month}
      onMonthChange={setMonth}
    />
  )
}

export default BusinessAtAGlance
